/**
 * confidentiality — coerce a doc-type's ``title_page.confidentiality`` into the
 * literal statement the Word title page renders.
 *
 * Extracted from ``loadRunIntoLiveDoc`` so the live-projection scaffold in
 * ``useWordWriteQueue`` can share the exact same coercion without importing the
 * whole per-section Load path (which pulls in the API client and wordWriter).
 * The two scaffold call sites MUST agree here, or the live doc and the Loaded
 * .docx render different cover pages.
 */

/** The doc-type YAML's ``title_page.confidentiality`` value. Historically a
 *  plain string; the M11 protocol title page migrated it to a structured
 *  mapping ``{ value, optional, suggested_default, ... }`` where ``value: null``
 *  means "no sponsor statement — OMIT the line" (``suggested_default`` is a
 *  human-facing review hint only and is NEVER emitted automatically). The
 *  server ships this block verbatim (server/routes/discovery.py), so the add-in
 *  must accept either shape. */
export type ConfidentialityConfig =
	| string
	| {
			value?: string | null;
			optional?: boolean;
			suggested_default?: string;
			use_suggested_default_only_when_sponsor_approves?: boolean;
	  }
	| null;

/** Resolve ``title_page.confidentiality`` (string or structured mapping) to the
 *  literal statement to render. Returns:
 *    • a string  → render this exact statement;
 *    • ``null``  → the doc-type configured confidentiality but supplied no
 *                  value → OMIT the line entirely (M11 title-page HARD RULE:
 *                  never auto-substitute ``suggested_default``);
 *    • ``undefined`` → not configured at all → let scaffold apply its generic
 *                  backward-compat default (e.g. CTD_M2, which sets no key).
 *
 *  This coercion is also what stops the scaffold crash: passing the raw mapping
 *  object to Office.js ``insertParagraph`` throws ``InvalidArgument``. */
export function resolveConfidentiality(
	raw: ConfidentialityConfig | undefined,
): string | null | undefined {
	if (raw === undefined) return undefined;
	if (typeof raw === "string") return raw.trim().length > 0 ? raw : null;
	if (raw && typeof raw === "object") {
		return typeof raw.value === "string" && raw.value.trim().length > 0
			? raw.value
			: null;
	}
	return null;
}
